import React, { useState, useEffect } from 'react';
import { Building2, AlertCircle } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { ModalShell, Spinner } from '../ui';


export default function InviteAcceptModal({
  show,
  inviteCode,
  walletAddress,
  walletConnected,
  authors,
  onConnectWallet,
  onAccept,
  onDecline,
  supabase
}) {
  const { t, isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [acceptError, setAcceptError] = useState('');
  const [selectedAuthorId, setSelectedAuthorId] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (show && inviteCode) {
      loadInvite();
    }
  }, [show, inviteCode]);

  useEffect(() => {
    if (authors && authors.length > 0 && !selectedAuthorId) {
      setSelectedAuthorId(authors[0].id);
    }
  }, [authors]);

  const loadInvite = async () => {
    setLoading(true);
    setLoadError('');

    try {
      // Load invite
      const { data: inviteData, error: inviteError } = await supabase
        .from('org_invites')
        .select('*')
        .eq('invite_code', inviteCode)
        .single();

      if (inviteError || !inviteData) {
        setLoadError('Invalid or expired invite link');
        setLoading(false);
        return;
      }

      // Check if expired
      if (inviteData.expires_at < Date.now()) {
        setLoadError('This invite link has expired');
        setLoading(false);
        return;
      }

      // Check if max uses reached
      if (inviteData.uses_count >= inviteData.max_uses) {
        setLoadError('This invite link has reached its maximum uses');
        setLoading(false);
        return;
      }

      setInvite(inviteData);

      // Load organization
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', inviteData.org_id)
        .single();

      if (orgError || !orgData) {
        setLoadError('Organization not found');
        setLoading(false);
        return;
      }

      setOrganization(orgData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading invite:', err);
      setLoadError('Failed to load invite details');
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!walletConnected) {
      setAcceptError('Please connect your wallet first');
      return;
    }

    if (authors.length === 0) {
      setAcceptError('Please create an author profile first');
      return;
    }

    if (!selectedAuthorId) {
      setAcceptError('Please select an author');
      return;
    }

    setAccepting(true);
    setAcceptError('');

    try {
      // Check if user already has a seat
      const { data: existingSeat } = await supabase
        .from('org_seats')
        .select('*')
        .eq('org_id', invite.org_id)
        .eq('wallet_address', walletAddress)
        .single();

      if (existingSeat) {
        setAcceptError('You already have access to this organization');
        setAccepting(false);
        return;
      }

      // Create seat
      const seatId = `seat_${Date.now()}`;
      const { error: seatError } = await supabase
        .from('org_seats')
        .insert({
          id: seatId,
          org_id: invite.org_id,
          wallet_address: walletAddress,
          author_id: selectedAuthorId,
          role: invite.role,
          created_at: Date.now()
        });

      if (seatError) throw seatError;

      // Update invite uses count
      const { error: updateError } = await supabase
        .from('org_invites')
        .update({
          uses_count: invite.uses_count + 1
        })
        .eq('id', invite.id);

      if (updateError) throw updateError;

      // Success
      onAccept(organization);
    } catch (err) {
      console.error('Error accepting invite:', err);
      setAcceptError('Failed to accept invite. Please try again.');
      setAccepting(false);
    }
  };

  if (!show) return null;

  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'owner': return 'Owner';
      case 'admin': return 'Admin';
      case 'guest': return 'Guest';
      default: return role;
    }
  };

  return (
    <ModalShell blur zIndex={999} panelStyle={{ backgroundColor: t.modalBg, padding: '40px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ margin: '0 auto 16px', width: '40px' }}>
              <Spinner size={40} />
            </div>
            <p style={{
              fontSize: '14px',
              color: t.textSecondary,
              fontFamily: 'Poppins, sans-serif'
            }}>
              Loading invite...
            </p>
          </div>
        ) : loadError ? (
          <div style={{ textAlign: 'center' }}>
            <AlertCircle size={64} style={{
              color: t.error,
              margin: '0 auto 24px',
              display: 'block'
            }} />
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: t.text,
              marginBottom: '12px',
              fontFamily: 'Poppins, sans-serif'
            }}>
              Invalid Invite
            </h2>
            <p style={{
              fontSize: '14px',
              color: t.textSecondary,
              marginBottom: '24px',
              fontFamily: 'Poppins, sans-serif'
            }}>
              {loadError}
            </p>
            <button
              onClick={onDecline}
              style={{
                backgroundColor: t.buttonBg,
                color: t.buttonText,
                padding: '12px 24px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Poppins, sans-serif'
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <Building2 size={64} style={{
                color: t.text,
                margin: '0 auto 16px',
                display: 'block'
              }} />
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: t.text,
                marginBottom: '8px',
                fontFamily: 'Poppins, sans-serif'
              }}>
                You've Been Invited!
              </h2>
              <p style={{
                fontSize: '14px',
                color: t.textSecondary,
                fontFamily: 'Poppins, sans-serif'
              }}>
                Join <strong>{organization?.name}</strong> as {getRoleDisplayName(invite?.role)}
              </p>
            </div>

            {/* Organization Info */}
            <div style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.borderStrong}`,
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <p style={{
                  fontSize: '12px',
                  color: t.textSecondary,
                  marginBottom: '4px',
                  fontFamily: 'Poppins, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Organization
                </p>
                <p style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: t.text,
                  fontFamily: 'Poppins, sans-serif'
                }}>
                  {organization?.name}
                </p>
                {organization?.bio && (
                  <p style={{
                    fontSize: '13px',
                    color: t.textSecondary,
                    marginTop: '4px',
                    fontFamily: 'Poppins, sans-serif'
                  }}>
                    {organization.bio}
                  </p>
                )}
              </div>

              <div>
                <p style={{
                  fontSize: '12px',
                  color: t.textSecondary,
                  marginBottom: '4px',
                  fontFamily: 'Poppins, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Your Role
                </p>
                <div style={{
                  display: 'inline-block',
                  backgroundColor: t.buttonBg,
                  color: t.buttonText,
                  padding: '6px 12px',
                  borderRadius: '999px',
                  fontSize: '12px',
                  fontWeight: '600',
                  fontFamily: 'Poppins, sans-serif'
                }}>
                  {getRoleDisplayName(invite?.role)}
                </div>
              </div>
            </div>

            {/* Wallet Connection */}
            {!walletConnected ? (
              <div style={{
                backgroundColor: t.surface,
                border: `1px solid ${t.borderStrong}`,
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: t.textSecondary,
                  marginBottom: '16px',
                  fontFamily: 'Poppins, sans-serif'
                }}>
                  Connect your wallet to accept this invitation
                </p>
                <button
                  onClick={() => {
                    onConnectWallet();
                    // Keep invite modal open, ConnectWalletModal will appear on top
                  }}
                  style={{
                    backgroundColor: t.buttonBg,
                    color: t.buttonText,
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Poppins, sans-serif',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  Connect Wallet
                </button>
              </div>
            ) : authors.length === 0 ? (
              <div style={{
                backgroundColor: t.surface,
                border: `1px solid ${t.borderStrong}`,
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: t.textSecondary,
                  fontFamily: 'Poppins, sans-serif'
                }}>
                  Please create an author profile first to join this organization
                </p>
              </div>
            ) : (
              <div style={{
                backgroundColor: t.surface,
                border: `1px solid ${t.borderStrong}`,
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <label style={{
                  fontSize: '12px',
                  color: t.textSecondary,
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Poppins, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Select Author to Connect
                </label>
                <select
                  value={selectedAuthorId || ''}
                  onChange={(e) => setSelectedAuthorId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: `1px solid ${t.borderStrong}`,
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif',
                    color: t.text,
                    backgroundColor: t.surface,
                    cursor: 'pointer'
                  }}
                >
                  {authors.map(author => (
                    <option key={author.id} value={author.id}>
                      {author.name}
                    </option>
                  ))}
                </select>
                <p style={{
                  fontSize: '12px',
                  color: t.textSecondary,
                  marginTop: '8px',
                  fontFamily: 'Poppins, sans-serif'
                }}>
                  This author will be linked to the organization
                </p>
              </div>
            )}

            {/* Accept Error - inline near buttons */}
            {acceptError && (
              <div style={{
                backgroundColor: t.errorBg,
                border: `1px solid ${t.error}`,
                color: t.error,
                padding: '10px 16px',
                borderRadius: '8px',
                marginBottom: '12px',
                fontSize: '13px',
                fontFamily: 'Poppins, sans-serif'
              }}>
                {acceptError}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onDecline}
                disabled={accepting}
                style={{
                  flex: 1,
                  backgroundColor: 'transparent',
                  color: t.text,
                  padding: '12px',
                  borderRadius: '6px',
                  border: `1px solid ${t.borderStrong}`,
                  cursor: accepting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Poppins, sans-serif',
                  opacity: accepting ? 0.5 : 1
                }}
              >
                Decline
              </button>
              <button
                onClick={handleAccept}
                disabled={!walletConnected || authors.length === 0 || accepting}
                style={{
                  flex: 1,
                  backgroundColor: t.buttonBg,
                  color: t.buttonText,
                  padding: '12px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: (!walletConnected || authors.length === 0 || accepting) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Poppins, sans-serif',
                  opacity: (!walletConnected || authors.length === 0 || accepting) ? 0.5 : 1
                }}
              >
                {accepting ? 'Accepting...' : 'Accept Invitation'}
              </button>
            </div>
          </>
        )}
    </ModalShell>
  );
}
